/**
 * TopupTiersEditor Component
 * Editor for wallet topup tier configuration
 */

import { Plus, Trash2 } from 'lucide-react';
import type { TopupTier } from '../types';
import { ChangeEvent } from 'react';

interface TopupTiersEditorProps {
    tiers: TopupTier[];
    onChange: (tiers: TopupTier[]) => void;
    disabled?: boolean;
    helperText?: string;
}

export function TopupTiersEditor({ tiers, onChange, disabled = false, helperText }: TopupTiersEditorProps) {
    const handleAddTier = () => {
        const lastTier = tiers[tiers.length - 1];
        const newAmount = lastTier ? lastTier.amount + 100 : 100;
        const newCredit = lastTier ? lastTier.credit + 110 : 110;
        onChange([...tiers, { amount: newAmount, credit: newCredit }]);
    };

    const handleRemoveTier = (index: number) => {
        const newTiers = [...tiers];
        newTiers.splice(index, 1);
        onChange(newTiers);
    };

    const handleTierChange = (index: number, field: 'amount' | 'credit', value: number) => {
        const newTiers = [...tiers];
        newTiers[index] = { ...newTiers[index], [field]: value };
        onChange(newTiers);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">แพ็กเกจเติมเงิน</label>
                <button
                    type="button"
                    onClick={handleAddTier}
                    disabled={disabled}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md text-sm transition-colors disabled:opacity-50"
                >
                    <Plus className="w-4 h-4" />
                    เพิ่มแพ็กเกจ
                </button>
            </div>

            {tiers.length === 0 ? (
                <p className="text-sm text-gray-500">ยังไม่มีแพ็กเกจเติมเงิน กดปุ่ม "เพิ่มแพ็กเกจ" เพื่อเพิ่ม</p>
            ) : (
                <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2 text-xs font-medium text-gray-500">
                        <span>จำนวนเงินเติม (บาท)</span>
                        <span>เครดิตที่ได้รับ</span>
                        <span></span>
                    </div>
                    {tiers.map((tier, index) => (
                        <div key={index} className="grid grid-cols-3 gap-2 items-center">
                            <input
                                type="number"
                                value={tier.amount}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => handleTierChange(index, 'amount', Number(e.target.value))}
                                disabled={disabled}
                                min={0}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:opacity-50 disabled:bg-gray-100"
                            />
                            <input
                                type="number"
                                value={tier.credit}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => handleTierChange(index, 'credit', Number(e.target.value))}
                                disabled={disabled}
                                min={0}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:opacity-50 disabled:bg-gray-100"
                            />
                            <button
                                type="button"
                                onClick={() => handleRemoveTier(index)}
                                disabled={disabled}
                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {helperText && (
                <p className="text-xs text-gray-500">{helperText}</p>
            )}
        </div>
    );
}
